import { useState, useRef, useCallback, useEffect } from 'react';

export const useMasteringEngine = () => {
    const audioContextRef = useRef(null);
    const [isInitialized, setIsInitialized] = useState(false);
    
    // Track state
    const [tracks, setTracks] = useState([]);
    const [activeTrackIndex, setActiveTrackIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    
    // Audio nodes refs
    const sourceNodesRef = useRef({});
    const gainNodesRef = useRef({});
    const masterChainRef = useRef(null);
    
    // Analysis state
    const [analysis, setAnalysis] = useState({
        peakLevel: -60,
        rmsLevel: -60,
        lufs: -23,
        dynamicRange: 0,
        lowFreqEnergy: 33,
        midFreqEnergy: 34,
        highFreqEnergy: 33,
        stereoWidth: 50
    });
    
    // Settings state
    const [settings, setSettings] = useState({
        // EQ
        eq_low_gain: 0,
        eq_low_freq: 100,
        eq_mid_gain: 0,
        eq_mid_freq: 1000,
        eq_mid_q: 1.0,
        eq_high_gain: 0,
        eq_high_freq: 8000,
        
        // Compressor
        compressor_threshold: -24,
        compressor_ratio: 4,
        compressor_attack: 10,
        compressor_release: 100,
        compressor_makeup: 0,
        
        // Multi-band
        multiband_enabled: false,
        multiband_low_threshold: -20,
        multiband_mid_threshold: -18,
        multiband_high_threshold: -16,
        
        // Limiter
        limiter_threshold: -1,
        limiter_ceiling: -0.3,
        limiter_release: 50,
        
        // Stereo
        stereo_width: 100,
        stereo_mid_side: 0,
        
        // Exciter
        exciter_enabled: false,
        exciter_drive: 0,
        exciter_frequency: 3000,
        
        // De-esser
        deesser_enabled: false,
        deesser_threshold: -30,
        deesser_frequency: 6000,
        deesser_ratio: 4,
        
        // Output
        output_gain: 0,
        normalize_lufs: -14
    });
    
    // Bypass state
    const [bypass, setBypass] = useState(false);
    
    // Animation frame ref
    const animationFrameRef = useRef(null);
    const startTimeRef = useRef(0);
    const analyserRef = useRef(null);
    
    // Initialize audio context
    const initAudio = useCallback(() => {
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            
            // Create analyser
            analyserRef.current = audioContextRef.current.createAnalyser();
            analyserRef.current.fftSize = 2048;
            analyserRef.current.connect(audioContextRef.current.destination);
            
            setIsInitialized(true);
        }
        
        if (audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume();
        }
        
        return audioContextRef.current;
    }, []);
    
    // Create master processing chain
    const createMasterChain = useCallback((ctx) => {
        // EQ
        const lowEQ = ctx.createBiquadFilter();
        lowEQ.type = 'lowshelf';
        lowEQ.frequency.value = settings.eq_low_freq;
        lowEQ.gain.value = settings.eq_low_gain;
        
        const midEQ = ctx.createBiquadFilter();
        midEQ.type = 'peaking';
        midEQ.frequency.value = settings.eq_mid_freq;
        midEQ.Q.value = settings.eq_mid_q;
        midEQ.gain.value = settings.eq_mid_gain;
        
        const highEQ = ctx.createBiquadFilter();
        highEQ.type = 'highshelf';
        highEQ.frequency.value = settings.eq_high_freq;
        highEQ.gain.value = settings.eq_high_gain;
        
        // Compressor
        const compressor = ctx.createDynamicsCompressor();
        compressor.threshold.value = settings.compressor_threshold;
        compressor.ratio.value = settings.compressor_ratio;
        compressor.attack.value = settings.compressor_attack / 1000;
        compressor.release.value = settings.compressor_release / 1000;
        
        // Limiter (another compressor with extreme settings)
        const limiter = ctx.createDynamicsCompressor();
        limiter.threshold.value = settings.limiter_threshold;
        limiter.ratio.value = 20;
        limiter.attack.value = 0.001;
        limiter.release.value = settings.limiter_release / 1000;
        
        // Output gain
        const outputGain = ctx.createGain();
        outputGain.gain.value = Math.pow(10, settings.output_gain / 20);
        
        // Connect chain
        lowEQ.connect(midEQ);
        midEQ.connect(highEQ);
        highEQ.connect(compressor);
        compressor.connect(limiter);
        limiter.connect(outputGain);
        
        return {
            input: lowEQ,
            output: outputGain,
            nodes: { lowEQ, midEQ, highEQ, compressor, limiter, outputGain }
        };
    }, [settings]);
    
    // Load track
    const loadTrack = useCallback(async (file, trackInfo) => {
        const ctx = initAudio();
        
        const arrayBuffer = await file.arrayBuffer();
        const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
        
        const newTrack = {
            ...trackInfo,
            buffer: audioBuffer,
            duration: audioBuffer.duration,
            volume: 1,
            pan: 0,
            muted: false,
            solo: false
        };
        
        setTracks(prev => [...prev, newTrack]);
        setDuration(prev => Math.max(prev, audioBuffer.duration));
        
        // Analyze audio
        analyzeAudio(audioBuffer);
        
        return newTrack;
    }, [initAudio]);
    
    // Analyze audio buffer
    const analyzeAudio = useCallback((buffer) => {
        const channelData = buffer.getChannelData(0);
        const sampleRate = buffer.sampleRate;
        
        // Peak level
        let peak = 0;
        let sumSquares = 0;
        
        for (let i = 0; i < channelData.length; i++) {
            const abs = Math.abs(channelData[i]);
            if (abs > peak) peak = abs;
            sumSquares += channelData[i] * channelData[i];
        }
        
        const rms = Math.sqrt(sumSquares / channelData.length);
        const peakDb = 20 * Math.log10(peak || 0.0001);
        const rmsDb = 20 * Math.log10(rms || 0.0001);
        const dynamicRange = peakDb - rmsDb;
        
        // Frequency analysis (simple approach)
        const fftSize = 4096;
        const numSamples = Math.min(channelData.length, sampleRate * 5); // Analyze 5 seconds
        
        let lowEnergy = 0;
        let midEnergy = 0;
        let highEnergy = 0;
        
        // Simple frequency band energy estimation
        for (let i = 0; i < numSamples - fftSize; i += fftSize) {
            const chunk = channelData.slice(i, i + fftSize);
            const energy = chunk.reduce((sum, s) => sum + s * s, 0);
            
            // Rough frequency estimation based on zero crossings
            let zeroCrossings = 0;
            for (let j = 1; j < chunk.length; j++) {
                if ((chunk[j] >= 0) !== (chunk[j-1] >= 0)) zeroCrossings++;
            }
            const freq = (zeroCrossings * sampleRate) / (2 * chunk.length);
            
            if (freq < 250) lowEnergy += energy;
            else if (freq < 4000) midEnergy += energy;
            else highEnergy += energy;
        }
        
        const totalEnergy = lowEnergy + midEnergy + highEnergy || 1;
        
        // Stereo width estimation
        let stereoWidth = 50;
        if (buffer.numberOfChannels >= 2) {
            const leftChannel = buffer.getChannelData(0);
            const rightChannel = buffer.getChannelData(1);
            let correlation = 0;
            const samples = Math.min(leftChannel.length, sampleRate * 5);
            
            for (let i = 0; i < samples; i++) {
                correlation += leftChannel[i] * rightChannel[i];
            }
            correlation /= samples;
            stereoWidth = Math.round((1 - correlation) * 100);
        }
        
        setAnalysis({
            peakLevel: Math.round(peakDb * 10) / 10,
            rmsLevel: Math.round(rmsDb * 10) / 10,
            lufs: Math.round((rmsDb - 3) * 10) / 10, // Rough LUFS estimation
            dynamicRange: Math.round(dynamicRange * 10) / 10,
            lowFreqEnergy: Math.round((lowEnergy / totalEnergy) * 100),
            midFreqEnergy: Math.round((midEnergy / totalEnergy) * 100),
            highFreqEnergy: Math.round((highEnergy / totalEnergy) * 100),
            stereoWidth
        });
    }, []);
    
    // Play
    const play = useCallback(() => {
        if (tracks.length === 0 || isPlaying) return;
        
        const ctx = initAudio();
        
        // Create master chain
        const chain = createMasterChain(ctx);
        chain.output.connect(analyserRef.current);
        masterChainRef.current = chain;
        
        // Create and start sources for all tracks
        tracks.forEach((track, index) => {
            if (track.buffer && !track.muted) {
                const source = ctx.createBufferSource();
                source.buffer = track.buffer;
                
                const gainNode = ctx.createGain();
                gainNode.gain.value = track.volume;
                
                source.connect(gainNode);
                
                if (bypass) {
                    gainNode.connect(analyserRef.current);
                } else {
                    gainNode.connect(chain.input);
                }
                
                source.start(0, currentTime);
                
                sourceNodesRef.current[index] = source;
                gainNodesRef.current[index] = gainNode;
                
                source.onended = () => {
                    if (sourceNodesRef.current[index] === source) {
                        delete sourceNodesRef.current[index];
                    }
                };
            }
        });
        
        startTimeRef.current = ctx.currentTime - currentTime;
        setIsPlaying(true);
    }, [tracks, isPlaying, currentTime, bypass, initAudio, createMasterChain]);
    
    // Pause
    const pause = useCallback(() => {
        if (!isPlaying) return;
        
        const ctx = audioContextRef.current;
        if (!ctx) return;
        
        // Stop all sources
        Object.values(sourceNodesRef.current).forEach(source => {
            try { source.stop(); } catch (e) {}
        });
        sourceNodesRef.current = {};
        gainNodesRef.current = {};
        
        setCurrentTime(ctx.currentTime - startTimeRef.current);
        setIsPlaying(false);
    }, [isPlaying]);
    
    // Stop
    const stop = useCallback(() => {
        pause();
        setCurrentTime(0);
    }, [pause]);
    
    // Seek
    const seek = useCallback((time) => {
        const wasPlaying = isPlaying;
        if (wasPlaying) pause();
        setCurrentTime(time);
        if (wasPlaying) setTimeout(play, 50);
    }, [isPlaying, pause, play]);
    
    // Update setting
    const updateSetting = useCallback((key, value) => {
        setSettings(prev => ({ ...prev, [key]: value }));
        
        // Update live nodes if playing
        if (masterChainRef.current?.nodes) {
            const nodes = masterChainRef.current.nodes;
            
            switch (key) {
                case 'eq_low_gain':
                    nodes.lowEQ.gain.value = value;
                    break;
                case 'eq_low_freq':
                    nodes.lowEQ.frequency.value = value;
                    break;
                case 'eq_mid_gain':
                    nodes.midEQ.gain.value = value;
                    break;
                case 'eq_mid_freq':
                    nodes.midEQ.frequency.value = value;
                    break;
                case 'eq_high_gain':
                    nodes.highEQ.gain.value = value;
                    break;
                case 'eq_high_freq':
                    nodes.highEQ.frequency.value = value;
                    break;
                case 'compressor_threshold':
                    nodes.compressor.threshold.value = value;
                    break;
                case 'compressor_ratio':
                    nodes.compressor.ratio.value = value;
                    break;
                case 'limiter_threshold':
                    nodes.limiter.threshold.value = value;
                    break;
                case 'output_gain':
                    nodes.outputGain.gain.value = Math.pow(10, value / 20);
                    break;
                default:
                    break;
            }
        }
    }, []);
    
    // Apply AI recommendations
    const applyRecommendations = useCallback((recommendations) => {
        if (recommendations.eq) {
            updateSetting('eq_low_gain', recommendations.eq.low_gain || 0);
            updateSetting('eq_mid_gain', recommendations.eq.mid_gain || 0);
            updateSetting('eq_high_gain', recommendations.eq.high_gain || 0);
        }
        if (recommendations.compression) {
            updateSetting('compressor_threshold', recommendations.compression.threshold || -24);
            updateSetting('compressor_ratio', recommendations.compression.ratio || 4);
            updateSetting('compressor_attack', recommendations.compression.attack || 10);
            updateSetting('compressor_release', recommendations.compression.release || 100);
        }
        if (recommendations.limiter) {
            updateSetting('limiter_threshold', recommendations.limiter.threshold || -1);
            updateSetting('limiter_ceiling', recommendations.limiter.ceiling || -0.3);
        }
        if (recommendations.stereo) {
            updateSetting('stereo_width', recommendations.stereo.width || 100);
        }
        if (recommendations.output) {
            updateSetting('output_gain', recommendations.output.gain || 0);
            updateSetting('normalize_lufs', recommendations.output.target_lufs || -14);
        }
    }, [updateSetting]);
    
    // Update current time
    useEffect(() => {
        const updateTime = () => {
            if (audioContextRef.current && isPlaying) {
                const time = audioContextRef.current.currentTime - startTimeRef.current;
                if (time >= duration) {
                    stop();
                } else {
                    setCurrentTime(time);
                }
            }
            animationFrameRef.current = requestAnimationFrame(updateTime);
        };
        
        animationFrameRef.current = requestAnimationFrame(updateTime);
        
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [isPlaying, duration, stop]);
    
    // Remove track
    const removeTrack = useCallback((index) => {
        if (isPlaying) stop();
        setTracks(prev => prev.filter((_, i) => i !== index));
    }, [isPlaying, stop]);
    
    // Update track
    const updateTrack = useCallback((index, updates) => {
        setTracks(prev => prev.map((track, i) => 
            i === index ? { ...track, ...updates } : track
        ));
        
        if (gainNodesRef.current[index] && updates.volume !== undefined) {
            gainNodesRef.current[index].gain.value = updates.volume;
        }
    }, []);
    
    // Get analyser for visualizations
    const getAnalyser = useCallback(() => analyserRef.current, []);
    
    return {
        isInitialized,
        initAudio,
        tracks,
        activeTrackIndex,
        setActiveTrackIndex,
        isPlaying,
        currentTime,
        duration,
        analysis,
        settings,
        bypass,
        setBypass,
        loadTrack,
        removeTrack,
        updateTrack,
        play,
        pause,
        stop,
        seek,
        updateSetting,
        applyRecommendations,
        getAnalyser
    };
};
